/**
 * <header-component>
 *
 * Web Component that owns all header behavior:
 *  - keeps CSS variables in sync via ResizeObserver (no resize-listener + setTimeout)
 *  - rAF-throttled scroll handler for scroll-driven classes, transparent toggle and auto-hide
 *  - menu open/close on hover + focus, body scroll lock, aria-expanded management
 *  - responsive menu overlap logic (menu drops to its own row when links wrap)
 *  - dispatches `menu:open` / listens for `menu:close`
 *
 * Loaded ONLY from `sections/main-header.liquid`. Does not live in global.js.
 *
 * External globals (accessed via window):
 *   - window.RevolutionSelectbox (global.js — selector widget for market/language)
 *   - @theme/body-scroll-lock
 *   - window.theme (theme.liquid inline — shared state for menu breakpoint)
 *
 * Public contract (do not break — CSS and other scripts depend on these):
 *   CSS vars set on documentElement:
 *     --header-height, --header-height-fixed, --submenu-position, --header-dynamic-height
 *   CSS classes:
 *     on this element: Header--Scrolled, Header--Transparent
 *     on parent:       header--Sticky
 *   Events:
 *     dispatches: menu:open
 *     listens:    menu:close (on body), drawer:opened/closed,
 *                 search:opening/closed, collection:state-restored
 *   Attributes:
 *     [data-has-menu] items get aria-expanded toggled
 */

import { bodyScrollLockOptions } from '@theme/body-scroll-lock-options';
import { RevolutionSelectbox } from '@theme/selectbox';
import { disableBodyScroll, enableBodyScroll } from '@theme/body-scroll-lock';
import { responsive } from '@theme/responsive';

const AUTO_HIDE_MIN_SAFE_ZONE = 100;
const AUTO_HIDE_REVEAL_DISTANCE = 50;

class HeaderComponent extends HTMLElement {
  #initialized = false;
  #options = { transparent: false };
  #scrollableElement = null;
  #lastScrollY = 0;
  #scrollDirection = '';
  #directionChangeY = 0;
  #autoHideSafeZone = AUTO_HIDE_MIN_SAFE_ZONE;
  #heroBanner = null;
  #openDrawerIds = new Set();
  #searchOpen = false;

  #resizeObserver = null;
  #scrollRafId = null;
  #menuOverlapRafId = null;
  #resizeCleanup = null;
  #headerSlideRafId = null;
  #headerSlideTimer = null;

  #menuTriggers = [];
  #submenuTargets = [];
  #singleMenuTargets = [];
  #focusOutTargets = [];
  #desktopMenuRequest = null;
  #desktopMenuHtml = null;
  #desktopMenuSources = null;
  #desktopMenuAbortController = null;
  #desktopMenuStylesheetRequest = null;
  #desktopMenuPrefetchCleanup = null;

  #onResize = () => {
    if (this.#menuOverlapRafId !== null) return;
    this.#menuOverlapRafId = requestAnimationFrame(() => {
      this.#menuOverlapRafId = null;
      this.#updateAutoHideSafeZone();
      this.#menuOverlapHandler();
    });
  };

  #onSectionLoad = () => {
    this.#attachMenuListeners();
    this.#updateAutoHideSafeZone();
    this.#menuOverlapHandler();
    this.#heroBanner = null;
    this.#updateScrollState();
  };

  #onSectionUnload = () => {
    this.#heroBanner = null;
    this.#updateScrollState();
  };

  #onScroll = () => {
    if (this.#scrollRafId !== null) return;
    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  // A restored PLP scroll is navigation state, not downward user intent. Reset
  // the auto-hide baseline and reveal the header before the restored frame paints.
  #onCollectionStateRestored = () => {
    const scrollY = window.scrollY;
    const parent = this.parentElement;
    const wasHidden = parent?.classList.contains('Header--Hidden') ?? false;

    this.#lastScrollY = scrollY;
    this.#directionChangeY = scrollY;
    this.#scrollDirection = '';
    parent?.classList.remove('Header--Hidden');

    if (wasHidden) this.#trackHeaderSlide();
    this.#syncHeaderDynamicHeight();
  };

  // The auto-hide slide (translateY) settles via a CSS transition; sync one last
  // time on its own end so --header-dynamic-height lands exactly on the header's
  // resting edge even if the safety timer hasn't fired yet.
  #onHeaderTransitionEnd = (event) => {
    if (event.target === this.parentElement && event.propertyName === 'transform') {
      this.#stopHeaderSlide();
    }
  };

  // Drawers overlay the page while the header strip remains visible. Keep that
  // strip solid for every drawer, not only the mobile menu, and track drawers by
  // id so closing a child drawer cannot restore transparency behind its parent.
  #onDrawerOpened = (event) => {
    const drawerId = event.target?.id;
    if (!drawerId) return;
    this.#openDrawerIds.add(drawerId);
    this.classList.remove('Header--Transparent');
    this.#updateScrollState();
  };

  #onDrawerClosed = (event) => {
    const drawerId = event.target?.id;
    if (!drawerId) return;
    this.#openDrawerIds.delete(drawerId);
    this.#updateScrollState();
  };

  #onSearchOpening = () => {
    this.#searchOpen = true;
    this.classList.remove('Header--Transparent');
    this.#updateScrollState();
  };

  #onSearchClosed = () => {
    this.#searchOpen = false;
    this.#updateScrollState();
  };

  connectedCallback() {
    // Guard against duplicate init if connectedCallback fires again without a
    // matching disconnect (e.g. the element being moved in the DOM).
    if (this.#initialized) return;
    this.#initialized = true;

    this.#options = this.#parseOptions();
    this.#scrollableElement = this.querySelector('[data-scrollable]');
    this.#lastScrollY = window.scrollY;
    this.#directionChangeY = this.#lastScrollY;
    this.#scrollDirection = '';

    // Global flags read by sibling sections (hero offset, sticky collection
    // bar, full-bleed video height). Set here instead of a Liquid-in-<style>
    // block so the stylesheet stays static and cacheable.
    const rootStyle = document.documentElement.style;
    rootStyle.setProperty('--header-is-transparent', this.#options.transparent ? '1' : '0');
    // Sibling sections (collection bar offset, lookbook) read this to know the
    // header doesn't hold a permanent slot — it pins but auto-hides on scroll.
    rootStyle.setProperty('--header-sticky', '0');

    this.#initSelectboxes();

    // Pin the header (so it can slide back in) but auto-hide it on scroll down
    // and reveal it on scroll up.
    this.parentElement?.classList.add('header--Sticky', 'header--AutoHide');

    this.#updateAutoHideSafeZone();
    this.#setupHeightObserver();

    // Scroll handler drives Header--Scrolled, the transparent toggle and auto-hide.
    document.addEventListener('scroll', this.#onScroll, { passive: true });
    document.addEventListener('collection:state-restored', this.#onCollectionStateRestored);

    // Finalize --header-dynamic-height the moment the auto-hide slide settles.
    this.parentElement?.addEventListener('transitionend', this.#onHeaderTransitionEnd);

    // Keep the header solid while any drawer or the desktop search overlay is open.
    document.addEventListener('drawer:opened', this.#onDrawerOpened);
    document.addEventListener('drawer:closed', this.#onDrawerClosed);
    document.addEventListener('search:opening', this.#onSearchOpening);
    document.addEventListener('search:closed', this.#onSearchClosed);

    document.body.addEventListener('menu:close', this.#closeMenu);
    this.#attachMenuListeners();
    this.#scheduleDesktopMenuPrefetch();

    this.#resizeCleanup = responsive.addResizeListener(this, this.#onResize);
    document.addEventListener('shopify:section:load', this.#onSectionLoad);
    document.addEventListener('shopify:section:unload', this.#onSectionUnload);

    this.#menuOverlapHandler();
    this.#updateScrollState();

    // Enable the background/colour transition only AFTER the initial state has
    // painted. base.css gates `transition` behind .Header--Ready so the first-frame
    // solid->transparent correction over a hero happens instantly instead of animating
    // as a 0.2s fade (the flash on refresh). Double rAF = "after the current state has
    // actually painted", so scroll/hover fades still animate from here on.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => this.classList.add('Header--Ready'))
    );
  }

  disconnectedCallback() {
    // Allow re-init if the element is reconnected (everything below is torn down).
    this.#initialized = false;

    this.#resizeObserver?.disconnect();

    if (this.#scrollRafId !== null) cancelAnimationFrame(this.#scrollRafId);
    if (this.#menuOverlapRafId !== null) cancelAnimationFrame(this.#menuOverlapRafId);
    this.#stopHeaderSlide();
    this.parentElement?.removeEventListener('transitionend', this.#onHeaderTransitionEnd);

    document.removeEventListener('scroll', this.#onScroll);
    document.removeEventListener('collection:state-restored', this.#onCollectionStateRestored);
    document.removeEventListener('drawer:opened', this.#onDrawerOpened);
    document.removeEventListener('drawer:closed', this.#onDrawerClosed);
    document.removeEventListener('search:opening', this.#onSearchOpening);
    document.removeEventListener('search:closed', this.#onSearchClosed);
    this.#openDrawerIds.clear();
    this.#searchOpen = false;
    this.#resizeCleanup?.removeResizeListener();
    this.#resizeCleanup = null;
    document.removeEventListener('shopify:section:load', this.#onSectionLoad);
    document.removeEventListener('shopify:section:unload', this.#onSectionUnload);
    document.body.removeEventListener('menu:close', this.#closeMenu);

    this.#detachMenuListeners();
    this.#desktopMenuAbortController?.abort();
    this.#desktopMenuPrefetchCleanup?.();
    this.#desktopMenuAbortController = null;
    this.#desktopMenuPrefetchCleanup = null;
    this.#desktopMenuRequest = null;
    this.#desktopMenuHtml = null;
    this.#desktopMenuSources = null;
    this.#desktopMenuStylesheetRequest = null;

    document.documentElement.style.removeProperty('--header-height');
    document.documentElement.style.removeProperty('--header-height-fixed');
    document.documentElement.style.removeProperty('--submenu-position');
    document.documentElement.style.removeProperty('--header-dynamic-height');
    document.documentElement.style.removeProperty('--header-is-transparent');
    document.documentElement.style.removeProperty('--header-sticky');
  }

  #parseOptions() {
    try {
      return JSON.parse(this.getAttribute('data-settings')) ?? {};
    } catch {
      return {};
    }
  }

  #initSelectboxes() {
    this.querySelectorAll('.RevolutionSelectbox').forEach(
      (item) => new RevolutionSelectbox(item)
    );
  }

  #setupHeightObserver() {
    this.#resizeObserver = new ResizeObserver(([entry]) => {
      if (!entry?.borderBoxSize?.[0]) return;
      const height = Math.round(entry.borderBoxSize[0].blockSize);
      const px = `${height}px`;
      const root = document.documentElement.style;
      root.setProperty('--header-height', px);
      // Do NOT rewrite --header-height-fixed here. That var drives the FWM/lookbook
      // pull-under margin; rewriting it after first paint (Liquid sets it) jumps the
      // hero. Repeats keeps --header-height-fixed Liquid-only for the same reason.
      root.setProperty('--submenu-position', px);
      this.#updateAutoHideSafeZone();
    });
    this.#resizeObserver.observe(this);
  }

  #attachMenuListeners() {
    this.#detachMenuListeners();

    this.#menuTriggers = Array.from(this.querySelectorAll('[data-has-menu]'));
    this.#menuTriggers.forEach((el) => {
      el.addEventListener('mouseenter', this.#openMenu);
      el.addEventListener('focusin', this.#openMenu);
    });

    this.#submenuTargets = [this];
    this.#submenuTargets.forEach((el) => {
      el.addEventListener('mouseleave', this.#closeMenu);
    });

    this.#singleMenuTargets = Array.from(this.querySelectorAll('[data-single-menu]'));
    this.#singleMenuTargets.forEach((el) => {
      el.addEventListener('mouseenter', this.#closeMenu);
    });

    this.#focusOutTargets = Array.from(
      this.querySelectorAll('[data-single-menu], .header-main-links')
    );
    this.#focusOutTargets.forEach((el) => {
      el.addEventListener('focusin', this.#closeMenu);
    });
  }

  #detachMenuListeners() {
    this.#menuTriggers.forEach((el) => {
      el.removeEventListener('mouseenter', this.#openMenu);
      el.removeEventListener('focusin', this.#openMenu);
    });
    this.#submenuTargets.forEach((el) => {
      el.removeEventListener('mouseleave', this.#closeMenu);
    });
    this.#singleMenuTargets.forEach((el) => {
      el.removeEventListener('mouseenter', this.#closeMenu);
    });
    this.#focusOutTargets.forEach((el) => {
      el.removeEventListener('focusin', this.#closeMenu);
    });
    this.#menuTriggers = [];
    this.#submenuTargets = [];
    this.#singleMenuTargets = [];
    this.#focusOutTargets = [];
  }

  #openMenu = async (event) => {
    const target = event.currentTarget;
    if (!(target instanceof HTMLElement)) return;

    // focusin bubbles from every link inside the dropdown. Only the top-level
    // header link should open/reset the menu; resetting when a collection card
    // receives focus hides that card before its click can complete.
    if (
      event.type === 'focusin' &&
      (!(event.target instanceof HTMLElement) ||
        !event.target.classList.contains('header-main-links'))
    ) {
      return;
    }

    this.dispatchEvent(new CustomEvent('menu:open', { bubbles: true }));

    this.#updateMenuPosition();

    if (this.classList.contains('Header--Transparent')) {
      this.classList.remove('Header--Transparent');
    }

    target.setAttribute('aria-expanded', 'true');

    const siblings = Array.from(target.parentElement?.children ?? []).filter(
      (child) => child !== target && child.getAttribute('aria-expanded') === 'true'
    );
    siblings.forEach((item) => {
      item.setAttribute('aria-expanded', 'false');
      const siblingWrapper = item.querySelector('.desktop-menu__dropdown-wrapper');
      if (siblingWrapper) siblingWrapper.style.display = 'none';
    });

    const wrapper = target.querySelector('.desktop-menu__dropdown-wrapper');
    if (!wrapper) return;

    try {
      await Promise.all([
        this.#hydrateDesktopMenu(target, wrapper),
        this.#loadDesktopMenuStylesheet(),
      ]);
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.error('Unable to load the desktop menu.', error);
      }
      target.setAttribute('aria-expanded', 'false');
      return;
    }

    // The pointer/focus may have left while the on-demand response was loading.
    if (!target.isConnected || target.getAttribute('aria-expanded') !== 'true') return;

    // Liquid keeps the lightweight shell inline-hidden to prevent a pre-JS flash.
    wrapper.style.display = '';

    // Wire up (once) and reset the mega-menu so its collection/promo columns and
    // sub-level panels are revealed correctly for this dropdown.
    const desktopDropdown = wrapper.querySelector('[data-desktop-dropdown]');
    if (desktopDropdown) this.setupDesktopMegaMenu(desktopDropdown);

    if (this.#scrollableElement && this.#scrollableElement !== wrapper) {
      enableBodyScroll(this.#scrollableElement);
    }
    this.#scrollableElement = wrapper;
    if (this.#scrollableElement) {
      disableBodyScroll(this.#scrollableElement, bodyScrollLockOptions);
    }
  };

  #closeMenu = () => {
    this.querySelectorAll('[data-has-menu][aria-expanded="true"]').forEach((item) => {
      item.setAttribute('aria-expanded', 'false');
    });

    this.querySelectorAll('.desktop-menu__dropdown-wrapper').forEach((wrapper) => {
      wrapper.style.display = 'none';
    });

    this.#updateScrollState();

    if (this.#scrollableElement) {
      enableBodyScroll(this.#scrollableElement);
    }
  };

  // ---------- Desktop mega-menu (second/third level + collection columns) ----------

  async #hydrateDesktopMenu(target, wrapper) {
    if (!wrapper.hasAttribute('data-menu-shell')) return;

    const menuHandle = target.getAttribute('data-menu-handle');
    if (!menuHandle) throw new Error('Desktop menu handle is missing.');

    const sources = await this.#loadDesktopMenuSources();
    const sourceWrapper = sources.get(menuHandle);
    const dropdown = sourceWrapper?.querySelector('[data-desktop-dropdown]');
    if (!dropdown) throw new Error(`Desktop menu "${menuHandle}" was not returned.`);

    wrapper.replaceChildren(dropdown.cloneNode(true));
    wrapper.removeAttribute('data-menu-shell');
  }

  #loadDesktopMenuSources() {
    if (this.#desktopMenuSources) return Promise.resolve(this.#desktopMenuSources);

    return this.#loadDesktopMenuHtml().then((sectionHtml) => {
      const documentFragment = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const sources = new Map();
      documentFragment.querySelectorAll('[data-has-menu][data-menu-handle]').forEach((item) => {
        const handle = item.getAttribute('data-menu-handle');
        const sourceWrapper = item.querySelector('.desktop-menu__dropdown-wrapper');
        if (handle && sourceWrapper) sources.set(handle, sourceWrapper);
      });
      if (!sources.size) throw new Error('Desktop menu response contains no dropdowns.');

      this.#desktopMenuSources = sources;
      return sources;
    });
  }

  #loadDesktopMenuHtml() {
    if (this.#desktopMenuHtml) return Promise.resolve(this.#desktopMenuHtml);
    if (this.#desktopMenuRequest) return this.#desktopMenuRequest;

    const sectionId = this.getAttribute('data-section-id');
    const renderPath = this.getAttribute('data-desktop-menu-path');
    if (!sectionId || !renderPath) {
      return Promise.reject(new Error('Desktop menu rendering configuration is missing.'));
    }

    const url = new URL(renderPath, window.location.origin);
    new URLSearchParams(window.location.search).forEach((value, key) => {
      if (key !== 'sections') url.searchParams.append(key, value);
    });
    url.searchParams.set('sections', sectionId);

    this.#desktopMenuAbortController = new AbortController();
    this.#desktopMenuRequest = fetch(url, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      signal: this.#desktopMenuAbortController.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Desktop menu request failed (${response.status}).`);
        return response.json();
      })
      .then((payload) => {
        const sectionHtml = payload?.[sectionId];
        if (!sectionHtml) throw new Error('Desktop menu section HTML is empty.');

        this.#desktopMenuHtml = sectionHtml;
        return sectionHtml;
      })
      .finally(() => {
        this.#desktopMenuRequest = null;
        this.#desktopMenuAbortController = null;
      });

    return this.#desktopMenuRequest;
  }

  #scheduleDesktopMenuPrefetch() {
    if (
      !responsive.matchesBreakpoint('desktop') ||
      this.querySelector('[data-desktop-dropdown]')
    ) {
      return;
    }

    const prefetch = () => {
      this.#desktopMenuPrefetchCleanup = null;
      this.#loadDesktopMenuHtml().catch(() => {});

      const stylesheetHref = this.getAttribute('data-desktop-menu-stylesheet');
      if (
        stylesheetHref &&
        !document.querySelector('link[data-desktop-menu-stylesheet-prefetch]')
      ) {
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.as = 'style';
        link.href = stylesheetHref;
        link.dataset.desktopMenuStylesheetPrefetch = '';
        document.head.appendChild(link);
      }
    };

    if ('requestIdleCallback' in window) {
      const idleId = window.requestIdleCallback(prefetch);
      this.#desktopMenuPrefetchCleanup = () => window.cancelIdleCallback(idleId);
      return;
    }

    const timeoutId = window.setTimeout(prefetch, 0);
    this.#desktopMenuPrefetchCleanup = () => window.clearTimeout(timeoutId);
  }

  #loadDesktopMenuStylesheet() {
    if (this.#desktopMenuStylesheetRequest) return this.#desktopMenuStylesheetRequest;

    const href = this.getAttribute('data-desktop-menu-stylesheet');
    if (!href) return Promise.reject(new Error('Desktop menu stylesheet URL is missing.'));

    const existingLink = document.querySelector('link[data-desktop-menu-stylesheet]');
    if (existingLink?.sheet) return Promise.resolve();

    const link = existingLink ?? document.createElement('link');
    if (!existingLink) {
      link.rel = 'stylesheet';
      link.href = href;
      link.dataset.desktopMenuStylesheet = '';
      document.head.appendChild(link);
    }

    this.#desktopMenuStylesheetRequest = new Promise((resolve, reject) => {
      link.addEventListener('load', resolve, { once: true });
      link.addEventListener('error', () => reject(new Error('Desktop menu stylesheet failed to load.')), {
        once: true,
      });
    });

    return this.#desktopMenuStylesheetRequest;
  }

  setupDesktopMegaMenu(dropdown) {
    if (!dropdown) return;
    // Bind hover/keyboard handlers once per dropdown; reset to root state on every open.
    if (dropdown.dataset.megaMenuReady !== 'true') {
      dropdown.dataset.megaMenuReady = 'true';
      dropdown.querySelectorAll('[data-second-key]').forEach((trigger) => {
        trigger.addEventListener('mouseenter', () =>
          this.activateDesktopSecondLevel(dropdown, trigger.getAttribute('data-second-key'))
        );
      });
      dropdown.querySelectorAll('[data-third-key]').forEach((trigger) => {
        trigger.addEventListener('mouseenter', () =>
          this.activateDesktopThirdLevel(dropdown, trigger.getAttribute('data-third-key'))
        );
      });
      dropdown.querySelectorAll('[data-second-clear]').forEach((trigger) => {
        trigger.addEventListener('mouseenter', () => this.clearDesktopSecondLevel(dropdown));
      });
      dropdown.addEventListener('keydown', (event) => this.handleDesktopMenuKeydown(event, dropdown));
    }
    this.clearDesktopMegaMenu(dropdown);
  }

  handleDesktopMenuKeydown(event, dropdown) {
    const { key } = event;
    const focusedEl = document.activeElement;
    if (!focusedEl || !dropdown.contains(focusedEl)) return;

    if (key === "Escape") {
      event.preventDefault();
      const menuTrigger = dropdown.closest("[aria-haspopup='true']");
      this.#closeMenu();
      if (menuTrigger) menuTrigger.focus();
      return;
    }

    if (key === "ArrowDown" || key === "ArrowUp") {
      event.preventDefault();
      const panel = focusedEl.closest(".desktop-menu__column, .desktop-menu__panel-list");
      if (!panel) return;
      const links = Array.from(
        panel.querySelectorAll(
          'a.desktop-menu__link:not([hidden]), a.desktop-menu__featured-link:not([hidden])'
        )
      );
      const visibleLinks = links.filter((link) => {
        let el = link;
        while (el && el !== dropdown) {
          if (el.hidden) return false;
          el = el.parentElement;
        }
        return true;
      });
      const currentIndex = visibleLinks.indexOf(focusedEl);
      if (currentIndex === -1) return;
      const nextIndex = key === "ArrowDown"
        ? (currentIndex + 1) % visibleLinks.length
        : (currentIndex - 1 + visibleLinks.length) % visibleLinks.length;
      visibleLinks[nextIndex]?.focus();
      return;
    }

    if (key === "ArrowRight") {
      event.preventDefault();
      const secondTrigger = focusedEl.closest("[data-second-key]");
      if (secondTrigger) {
        this.activateDesktopSecondLevel(dropdown, secondTrigger.getAttribute("data-second-key"));
        return;
      }
      const thirdTrigger = focusedEl.closest("[data-third-key]");
      if (thirdTrigger) {
        this.activateDesktopThirdLevel(dropdown, thirdTrigger.getAttribute("data-third-key"));
        return;
      }
    }

    if (key === "ArrowLeft") {
      event.preventDefault();
      const thirdPanel = focusedEl.closest("[data-third-panel]:not([hidden]), [data-second-overflow-panel]:not([hidden])");
      if (thirdPanel) {
        this.clearDesktopThirdLevel(dropdown);
        const activeSecondTrigger = dropdown.querySelector("[data-second-key].is-active");
        if (activeSecondTrigger) activeSecondTrigger.focus();
        return;
      }
      const secondPanel = focusedEl.closest("[data-second-panel]:not([hidden])");
      if (secondPanel) {
        this.clearDesktopSecondLevel(dropdown);
        const secondCol = dropdown.querySelector(".desktop-menu__column--second");
        const firstLink = secondCol?.querySelector('a');
        if (firstLink) firstLink.focus();
        return;
      }
    }
  }

  activateDesktopSecondLevel(dropdown, secondKey) {
    if (!dropdown || !secondKey) return;

    const secondTriggers = dropdown.querySelectorAll("[data-second-key]");
    secondTriggers.forEach((trigger) => {
      const isActive = trigger.getAttribute("data-second-key") === secondKey;
      trigger.classList.toggle("is-active", isActive);
      trigger.setAttribute("aria-expanded", String(isActive));
    });

    const secondPanels = dropdown.querySelectorAll("[data-second-panel]");
    let activeSecondPanel = null;
    secondPanels.forEach((panel) => {
      const isActive = panel.getAttribute("data-second-panel") === secondKey;
      panel.hidden = !isActive;
      if (isActive) activeSecondPanel = panel;
    });

    const thirdColumn = dropdown.querySelector('[data-column="third"]');
    const hasThirdColumnContent = !!(
      activeSecondPanel &&
      activeSecondPanel.querySelector(".desktop-menu__link")
    );
    this.clearDesktopThirdLevel(dropdown);
    const hasSecondLevelOverflow = this.showDesktopSecondLevelOverflow(dropdown, secondKey);
    const pseudoThirdColumn = dropdown.querySelector('[data-column="pseudo-third"]');
    if (pseudoThirdColumn) {
      pseudoThirdColumn.hidden = !hasSecondLevelOverflow;
    }

    const hasSecondLevelCollection = this.showDesktopSecondLevelCollection(dropdown, secondKey);
    const collectionColumn = dropdown.querySelector('[data-column="collection"]');
    if (collectionColumn) {
      collectionColumn.hidden = !hasSecondLevelCollection;
    }

    if (thirdColumn) {
      thirdColumn.hidden = !hasThirdColumnContent && !hasSecondLevelCollection;
    }

    if (hasSecondLevelOverflow) {
      this.updateDesktopMenuCols(dropdown, 4);
      this.focusFirstLinkInPanel(dropdown, `[data-second-panel="${secondKey}"]:not([hidden])`);
      return;
    }
    this.updateDesktopMenuCols(dropdown, "split");
    this.focusFirstLinkInPanel(dropdown, `[data-second-panel="${secondKey}"]:not([hidden])`);
  }

  activateDesktopThirdLevel(dropdown, thirdKey) {
    if (!dropdown || !thirdKey) return;

    const thirdTriggers = dropdown.querySelectorAll("[data-third-key]");
    thirdTriggers.forEach((trigger) => {
      const isActive = trigger.getAttribute("data-third-key") === thirdKey;
      trigger.classList.toggle("is-active", isActive);
      trigger.setAttribute("aria-expanded", String(isActive));
    });

    const parentSecondKey = thirdKey.replace(/-t-\d+$/, "");
    const activeSecondPanel = dropdown.querySelector(`[data-second-panel="${parentSecondKey}"]`);
    const isSecondLevelSplit = !!(
      activeSecondPanel &&
      activeSecondPanel.getAttribute("data-second-split") === "true"
    );

    if (isSecondLevelSplit) {
      const hasSecondLevelOverflow = this.showDesktopSecondLevelOverflow(dropdown, parentSecondKey);
      const pseudoThirdColumn = dropdown.querySelector('[data-column="pseudo-third"]');
      if (pseudoThirdColumn) {
        pseudoThirdColumn.hidden = !hasSecondLevelOverflow;
      }

      const hasThirdSplitContent = this.showDesktopThirdSplitPanel(dropdown, thirdKey);

      if (hasThirdSplitContent) {
        this.hideDesktopCollectionPanels(dropdown);
      }

      const hasThirdLevelCollection = hasThirdSplitContent
        ? false
        : this.showDesktopThirdLevelCollection(dropdown, thirdKey);

      const collectionColumn = dropdown.querySelector('[data-column="collection"]');
      if (collectionColumn) {
        collectionColumn.hidden = !hasThirdSplitContent && !hasThirdLevelCollection;
      }
      this.updateDesktopMenuCols(dropdown, 4);
      return;
    }

    this.hideDesktopSecondLevelOverflow(dropdown);
    this.hideDesktopThirdSplitPanels(dropdown);

    const thirdPanels = dropdown.querySelectorAll("[data-third-panel]");
    let hasPseudoThirdContent = false;
    thirdPanels.forEach((panel) => {
      const isActive = panel.getAttribute("data-third-panel") === thirdKey;
      panel.hidden = !isActive;
      if (isActive && panel.children.length > 0) {
        hasPseudoThirdContent = true;
      }
    });
    const pseudoThirdColumn = dropdown.querySelector('[data-column="pseudo-third"]');
    if (pseudoThirdColumn) {
      pseudoThirdColumn.hidden = !hasPseudoThirdContent;
    }

    if (hasPseudoThirdContent) {
      this.hideDesktopCollectionPanels(dropdown);
    }

    const hasThirdLevelCollection = hasPseudoThirdContent
      ? false
      : this.showDesktopThirdLevelCollection(dropdown, thirdKey);

    const collectionColumn = dropdown.querySelector('[data-column="collection"]');
    if (collectionColumn) {
      collectionColumn.hidden = !hasPseudoThirdContent && !hasThirdLevelCollection;
    }
    this.updateDesktopMenuCols(dropdown, hasPseudoThirdContent ? 4 : "split");
    this.focusFirstLinkInPanel(dropdown, `[data-third-panel="${thirdKey}"]:not([hidden])`);
  }

  focusFirstLinkInPanel(dropdown, panelSelector) {
    const wrapper = dropdown.closest('[data-submenu]');
    if (!wrapper || !wrapper.contains(document.activeElement)) return;
    const panel = dropdown.querySelector(panelSelector);
    const firstLink = panel?.querySelector('a');
    if (firstLink) requestAnimationFrame(() => firstLink.focus());
  }

  clearDesktopThirdLevel(dropdown) {
    if (!dropdown) return;

    dropdown.querySelectorAll("[data-third-key]").forEach((trigger) => {
      trigger.classList.remove("is-active");
      trigger.setAttribute("aria-expanded", "false");
    });
    dropdown.querySelectorAll("[data-third-panel]").forEach((panel) => {
      panel.hidden = true;
    });
    this.hideDesktopThirdSplitPanels(dropdown);
    dropdown.querySelectorAll("[data-collection-third-panel]").forEach((panel) => {
      panel.hidden = true;
    });
    const pseudoThirdColumn = dropdown.querySelector('[data-column="pseudo-third"]');
    if (pseudoThirdColumn) {
      pseudoThirdColumn.hidden = true;
    }
  }

  clearDesktopSecondLevel(dropdown) {
    if (!dropdown) return;

    dropdown.querySelectorAll("[data-second-key]").forEach((trigger) => {
      trigger.classList.remove("is-active");
      trigger.setAttribute("aria-expanded", "false");
    });
    dropdown.querySelectorAll("[data-second-panel]").forEach((panel) => {
      panel.hidden = true;
    });
    this.hideDesktopSecondLevelOverflow(dropdown);

    const thirdColumn = dropdown.querySelector('[data-column="third"]');
    if (thirdColumn) thirdColumn.hidden = true;
    this.clearDesktopThirdLevel(dropdown);

    const collectionColumn = dropdown.querySelector('[data-column="collection"]');
    if (collectionColumn) collectionColumn.hidden = true;
    dropdown.querySelectorAll("[data-collection-second-panel]").forEach((panel) => {
      panel.hidden = true;
    });
    dropdown.querySelectorAll("[data-collection-third-panel]").forEach((panel) => {
      panel.hidden = true;
    });
    const rootCollectionPanel = dropdown.querySelector("[data-collection-root-panel]");
    const hasRootCollectionContent = !!(rootCollectionPanel && rootCollectionPanel.children.length > 0);
    if (rootCollectionPanel) {
      rootCollectionPanel.hidden = !hasRootCollectionContent;
    }
    if (collectionColumn && hasRootCollectionContent) {
      collectionColumn.hidden = false;
      this.updateDesktopMenuCols(dropdown, 2);
      return;
    }
    this.updateDesktopMenuCols(dropdown, 1);
  }

  clearDesktopMegaMenu(dropdown) {
    this.clearDesktopSecondLevel(dropdown);
  }

  showDesktopSecondLevelCollection(dropdown, secondKey) {
    if (!dropdown || !secondKey) return false;

    let hasCollectionContent = false;
    dropdown.querySelectorAll("[data-collection-second-panel]").forEach((panel) => {
      const isActive = panel.getAttribute("data-collection-second-panel") === secondKey;
      const hasContent = panel.children.length > 0;
      panel.hidden = !isActive || !hasContent;
      if (isActive && hasContent) {
        hasCollectionContent = true;
      }
    });
    dropdown.querySelectorAll("[data-collection-third-panel]").forEach((panel) => {
      panel.hidden = true;
    });
    const rootCollectionPanel = dropdown.querySelector("[data-collection-root-panel]");
    const hasRootCollectionContent = !!(rootCollectionPanel && rootCollectionPanel.children.length > 0);
    const showRootCollection = !hasCollectionContent && hasRootCollectionContent;
    if (rootCollectionPanel) {
      rootCollectionPanel.hidden = !showRootCollection;
    }
    const collectionColumn = dropdown.querySelector('[data-column="collection"]');
    if (collectionColumn) {
      collectionColumn.hidden = !hasCollectionContent && !showRootCollection;
    }
    return hasCollectionContent || showRootCollection;
  }

  showDesktopThirdLevelCollection(dropdown, thirdKey) {
    if (!dropdown || !thirdKey) return false;

    let hasCollectionContent = false;
    dropdown.querySelectorAll("[data-collection-third-panel]").forEach((panel) => {
      const isActive = panel.getAttribute("data-collection-third-panel") === thirdKey;
      const hasContent = panel.children.length > 0;
      panel.hidden = !isActive || !hasContent;
      if (isActive && hasContent) {
        hasCollectionContent = true;
      }
    });

    const parentSecondKey = thirdKey.replace(/-t-\d+$/, "");
    let hasParentCollectionContent = false;
    dropdown.querySelectorAll("[data-collection-second-panel]").forEach((panel) => {
      const isParent = panel.getAttribute("data-collection-second-panel") === parentSecondKey;
      const hasContent = panel.children.length > 0;
      const showParentCollection = !hasCollectionContent && isParent && hasContent;
      panel.hidden = !showParentCollection;
      if (showParentCollection) {
        hasParentCollectionContent = true;
      }
    });

    const rootCollectionPanel = dropdown.querySelector("[data-collection-root-panel]");
    const hasRootCollectionContent = !!(rootCollectionPanel && rootCollectionPanel.children.length > 0);
    const showRootCollection =
      !hasCollectionContent && !hasParentCollectionContent && hasRootCollectionContent;
    if (rootCollectionPanel) {
      rootCollectionPanel.hidden = !showRootCollection;
    }
    return hasCollectionContent || hasParentCollectionContent || showRootCollection;
  }

  showDesktopSecondLevelOverflow(dropdown, secondKey) {
    if (!dropdown || !secondKey) return false;

    let hasOverflowContent = false;
    dropdown.querySelectorAll("[data-second-overflow-panel]").forEach((panel) => {
      const isActive = panel.getAttribute("data-second-overflow-panel") === secondKey;
      panel.hidden = !isActive;
      if (isActive && panel.children.length > 0) {
        hasOverflowContent = true;
      }
    });
    return hasOverflowContent;
  }

  hideDesktopSecondLevelOverflow(dropdown) {
    if (!dropdown) return;
    dropdown.querySelectorAll("[data-second-overflow-panel]").forEach((panel) => {
      panel.hidden = true;
    });
  }

  showDesktopThirdSplitPanel(dropdown, thirdKey) {
    if (!dropdown || !thirdKey) return false;

    let hasThirdSplitContent = false;
    dropdown.querySelectorAll("[data-third-split-panel]").forEach((panel) => {
      const isActive = panel.getAttribute("data-third-split-panel") === thirdKey;
      panel.hidden = !isActive;
      if (isActive && panel.children.length > 0) {
        hasThirdSplitContent = true;
      }
    });
    return hasThirdSplitContent;
  }

  hideDesktopThirdSplitPanels(dropdown) {
    if (!dropdown) return;
    dropdown.querySelectorAll("[data-third-split-panel]").forEach((panel) => {
      panel.hidden = true;
    });
  }

  hideDesktopCollectionPanels(dropdown) {
    if (!dropdown) return;
    dropdown.querySelectorAll("[data-collection-second-panel]").forEach((panel) => {
      panel.hidden = true;
    });
    dropdown.querySelectorAll("[data-collection-third-panel]").forEach((panel) => {
      panel.hidden = true;
    });
    const rootCollectionPanel = dropdown.querySelector("[data-collection-root-panel]");
    if (rootCollectionPanel) {
      rootCollectionPanel.hidden = true;
    }
  }

  updateDesktopMenuCols(dropdown, cols) {
    if (!dropdown) return;
    dropdown.setAttribute("data-cols", String(cols));
  }

  #updateMenuPosition() {
    document.documentElement.style.setProperty(
      '--submenu-position',
      `${this.offsetHeight}px`
    );
  }

  #updateScrollState() {
    const scrollY = window.scrollY;

    if (scrollY > 25) {
      this.classList.add('Header--Scrolled');
    } else {
      this.classList.remove('Header--Scrolled');
    }

    if (this.#options.transparent) {
      if (this.#searchOpen || this.#openDrawerIds.size > 0) {
        // An overlay is open — keep the header solid regardless of hero.
        this.classList.remove('Header--Transparent');
      } else {
        // Transparent only while the header overlaps the hero MEDIA (the image/video) —
        // once the hero has scrolled past the header bottom, fall back to a background.
        // On lookbook pages this targets just the hero, not the whole section, so the
        // header turns solid as soon as you leave the hero (over the tabs/grid).
        //
        // Only an OVERLAP hero counts: the Full Width Media section adds `.hero--overlap`
        // solely when "Regular section" is OFF (hero_banner=false). Lookbook uses
        // `.lookbook-header__media--hero` only when the header section has real media —
        // so disabling/emptying the lookbook header keeps the top bar solid.
        // A regular banner-hero must NOT make the top bar transparent — otherwise it
        // keeps the light over-hero colours over a normal (light) section and the
        // links go invisible. The section owns this decision via that class; the
        // header just respects it.
        let hero = this.#heroBanner;
        if (!hero || !hero.isConnected) {
          hero = this.#heroBanner = document.querySelector(
            'banner-hero.hero--overlap, .lookbook-header__media--hero',
          );
        }
        const overHero = hero
          ? hero.getBoundingClientRect().bottom > this.offsetHeight
          : false;
        if (overHero) {
          this.classList.add('Header--Transparent');
        } else {
          this.classList.remove('Header--Transparent');
        }
      }
    }

    // Match the reference header's intentional scroll behavior: keep the bar
    // visible until its complete announcement + header layout slot has cleared
    // the viewport, then hide on scroll down. Require a meaningful upward move
    // before revealing so small touch/trackpad reversals do not make it flicker.
    const parent = this.parentElement;
    const wasHidden = parent?.classList.contains('Header--Hidden') ?? false;
    let nextDirection = this.#scrollDirection;
    if (scrollY > this.#lastScrollY) {
      nextDirection = 'down';
    } else if (scrollY < this.#lastScrollY) {
      nextDirection = 'up';
    }
    if (nextDirection !== this.#scrollDirection) {
      this.#scrollDirection = nextDirection;
      this.#directionChangeY = scrollY;
    }

    const pastSafeZone = scrollY >= this.#autoHideSafeZone;
    const hasRevealedDistance =
      this.#scrollDirection === 'up' &&
      this.#directionChangeY - scrollY >= AUTO_HIDE_REVEAL_DISTANCE;
    const overlayOpen = this.#searchOpen || this.#openDrawerIds.size > 0;

    if (!pastSafeZone || overlayOpen) {
      parent?.classList.remove('Header--Hidden');
    } else if (this.#scrollDirection === 'down') {
      parent?.classList.add('Header--Hidden');
    } else if (hasRevealedDistance) {
      parent?.classList.remove('Header--Hidden');
    }
    this.#lastScrollY = scrollY;

    // The slide runs as a 0.3s CSS transform transition, but scroll events (our
    // only trigger to re-sample the visible height) stop the instant the user
    // lifts their finger. Track the transition frame-by-frame so sticky siblings
    // that pin to --header-dynamic-height (collection/search filter bar) stay
    // glued to the header's moving bottom edge instead of detaching mid-slide.
    if ((parent?.classList.contains('Header--Hidden') ?? false) !== wasHidden) {
      this.#trackHeaderSlide();
    }

    this.#syncHeaderDynamicHeight();
  }

  // The announcement bar sits before the sticky header in normal flow. Hiding
  // before both layout slots have scrolled away exposes their reserved space as
  // a white strip. Measure the rendered bars (translations may wrap) while
  // retaining the reference's 100px minimum safe zone.
  #updateAutoHideSafeZone() {
    const announcementHeight =
      document.querySelector('announcement-bar[data-main="true"]')?.offsetHeight ?? 0;
    this.#autoHideSafeZone = Math.max(
      AUTO_HIDE_MIN_SAFE_ZONE,
      Math.ceil(announcementHeight + this.offsetHeight)
    );
  }

  // Publish the header's currently visible height (clamped to the viewport) so
  // siblings can offset against the part of the header that's actually on screen.
  #syncHeaderDynamicHeight() {
    const rect = this.getBoundingClientRect();
    const visibleHeight = Math.max(
      0,
      Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0)
    );
    document.documentElement.style.setProperty(
      '--header-dynamic-height',
      `${Math.round(visibleHeight)}px`
    );
  }

  // Re-sample the visible height every frame for the duration of the auto-hide
  // slide. transitionend stops it as soon as the header settles; the timer is a
  // safety net for cases where transitionend never fires (reduced motion, an
  // interrupted/retargeted transition).
  #trackHeaderSlide() {
    if (this.#headerSlideRafId === null) {
      const step = () => {
        this.#syncHeaderDynamicHeight();
        this.#headerSlideRafId = requestAnimationFrame(step);
      };
      this.#headerSlideRafId = requestAnimationFrame(step);
    }
    if (this.#headerSlideTimer !== null) clearTimeout(this.#headerSlideTimer);
    // Slightly longer than the 0.3s transform transition.
    this.#headerSlideTimer = setTimeout(() => this.#stopHeaderSlide(), 360);
  }

  #stopHeaderSlide() {
    if (this.#headerSlideRafId !== null) {
      cancelAnimationFrame(this.#headerSlideRafId);
      this.#headerSlideRafId = null;
    }
    if (this.#headerSlideTimer !== null) {
      clearTimeout(this.#headerSlideTimer);
      this.#headerSlideTimer = null;
    }
    this.#syncHeaderDynamicHeight();
  }

  #menuOverlapHandler() {
    const menuItems = this.querySelector('.header-menu-items');
    const menuWrapper = this.querySelector('.header--Wrapper');
    if (!menuItems || !menuWrapper) return;

    const currentClientWidth = Math.max(
      document.body.scrollWidth,
      document.documentElement.scrollWidth,
      document.body.offsetWidth,
      document.documentElement.offsetWidth,
      document.documentElement.clientWidth
    );

    const heights = [];
    this.querySelectorAll('.header-main-links').forEach((item) =>
      heights.push(item.offsetHeight)
    );

    const maxHeight = heights.length ? Math.max.apply(null, heights) : 0;
    const minHeight = heights.length ? Math.min.apply(null, heights) : 0;
    const overlapping = maxHeight > minHeight;

    window.theme = window.theme || {};

    if (overlapping && (!window.theme.menuBreakpoint || window.theme.menuBreakpoint === 0)) {
      window.theme.menuBreakpoint = currentClientWidth;
    }

    if (currentClientWidth > (window.theme.menuBreakpoint || 0)) {
      menuItems.classList.remove('mt-m', 'mainMenu--Overlapping');
      menuWrapper.querySelector('.header-main-menu-items')?.prepend(menuItems);
      window.theme.menuBreakpoint = 0;
    } else if (overlapping) {
      menuItems.classList.add('mt-m', 'mainMenu--Overlapping');
      menuItems.closest('.header')?.appendChild(menuItems);
    }
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}
